/**
 * Mutual exclusivity for "none of the above" on multi-select checklists.
 * Matches English + Spanish MDI option labels (option value and/or title).
 */

const NONE_OF_THE_ABOVE_LABELS = new Set([
  "none of the above",
  "ninguna de las anteriores",
  "ninguna de los anteriores",
  "ninguno de los anteriores",
]);

function normalizeLabel(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, " ");
}

export function isNoneOfTheAboveText(text: string | null | undefined): boolean {
  if (!text) return false;
  return NONE_OF_THE_ABOVE_LABELS.has(normalizeLabel(text));
}

export function isNoneOfTheAboveOption(option: {
  option: string;
  title?: string | null;
}): boolean {
  return isNoneOfTheAboveText(option.option) || isNoneOfTheAboveText(option.title);
}

export function getNoneOfTheAboveValues(
  options: { option: string; title?: string | null }[]
): string[] {
  return options.filter(isNoneOfTheAboveOption).map((opt) => opt.option);
}

export function toggleMultipleOptionWithNoneExclusivity(
  selectedValues: string[],
  optionValue: string,
  checked: boolean,
  noneOptionValues: string[]
): string[] {
  if (checked) {
    if (noneOptionValues.includes(optionValue)) {
      return [optionValue];
    }
    return [...selectedValues.filter((v) => !noneOptionValues.includes(v)), optionValue];
  }
  return selectedValues.filter((v) => v !== optionValue);
}

export function isOnlyNoneOfTheAboveSelected(
  selectedValues: string[] | undefined | null,
  noneOptionValues: string[]
): boolean {
  if (!Array.isArray(selectedValues) || noneOptionValues.length === 0) return false;
  return selectedValues.length === 1 && noneOptionValues.includes(selectedValues[0]);
}

export function sanitizeMultipleOptionAnswer(
  selectedValues: string[],
  noneOptionValues: string[]
): string[] {
  if (noneOptionValues.length === 0) return selectedValues;

  const hasNone = selectedValues.some((v) => noneOptionValues.includes(v));
  const hasOther = selectedValues.some((v) => !noneOptionValues.includes(v));

  if (hasNone && hasOther) {
    return selectedValues.filter((v) => !noneOptionValues.includes(v));
  }
  return selectedValues;
}
